/**
 * Standardized error state pattern for client portal.
 * Pattern: headline, reason, action, support.
 * Use for page-level and section-level error surfaces.
 */
import React from 'react';

const DEFAULT_SUPPORT_EMAIL = 'support@voluntas.com';

export interface ErrorStateProps {
  /** Short error headline (e.g. "Unable to load data") */
  headline: string;
  /** Explanation of what went wrong */
  reason?: string;
  /** Primary recovery action (e.g. Retry button) */
  action?: React.ReactNode;
  /** Support contact link; defaults to mailto:support@voluntas.com (mailbox unchanged until infra migration) */
  supportHref?: string;
  /** Layout: banner (compact inline) or card (full section) */
  variant?: 'banner' | 'card';
  className?: string;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  headline,
  reason,
  action,
  supportHref = `mailto:${DEFAULT_SUPPORT_EMAIL}`,
  variant = 'card',
  className = ''
}) => {
  const supportLink = (
    <a
      href={supportHref}
      className="text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded"
    >
      Contact Support
    </a>
  );

  if (variant === 'banner') {
    return (
      <div
        className={`rounded-xl border border-error-200 bg-error-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 ${className}`}
        role="alert"
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-error-800">{headline}</p>
          {reason && <p className="text-sm text-error-700 mt-1">{reason}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {action}
          {supportLink}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl border border-primary-200 bg-primary-50/30 p-6 ${className}`}
      role="alert"
    >
      <h2 className="text-lg font-semibold text-primary-800 mb-2">{headline}</h2>
      {reason && <p className="text-primary-600 mb-4">{reason}</p>}
      <div className="flex flex-wrap items-center gap-3">
        {action}
        {supportLink}
      </div>
    </div>
  );
};

export default ErrorState;
